import { FC, useEffect, useMemo, useRef } from 'react';
import { AText, Box, GridItem } from '@alf/uikit';
import { LOG_ACTIONS, logAction } from '@namespace/analytics';
import { useConfig, useGeneralRoutes } from '@namespace/cms';
import { useDeviceStore } from '@namespace/device';
import { LocalizedNavLink, useI18n } from '@namespace/i18n';
// TODO: remove comment when prettier will be updated
import type { SportIconId } from '@namespace/icons2';
import { getSportIcon, Icon } from '@namespace/icons2';
import { useRouteParams } from '@namespace/router';
import { useVirtualSportsStore } from '../../store';
import { IVirtualSportsGame } from '../../types';
import { IVirtualSportsLobbyMenuItem } from './types';

import styles from './index.module.css';

interface IProps {
  id: string;
}

const LobbyMenu: FC<IProps> = ({ id }) => {
  const { config } = useConfig(id);
  const { hideForProviders = [] } = config;
  const { isDesktop, isMobile } = useDeviceStore();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const { virtualSportsFrame } = useGeneralRoutes();
  const { feed } = useVirtualSportsStore(['feed']);
  const { t } = useI18n();
  const { provider } = useRouteParams(
    `${virtualSportsFrame}/:provider/:categoryId/*`
  );
  const { providers, games, isLoaded: isFeedLoaded } = feed;

  useEffect(() => {
    const parentElement = wrapperRef.current?.parentNode as HTMLElement | null;

    if (parentElement) {
      if (hideForProviders.includes(provider)) {
        // some shit for betradar
        parentElement.style.display = 'none';
      } else {
        parentElement.style.display = '';
      }
    }
  }, [hideForProviders, provider]);

  const sports: IVirtualSportsGame[] = useMemo(() => {
    // @ts-expect-error TODO: move useVirtualSportsStore to TypeScript
    const feedProviderGames = providers.data?.[provider]?.games;

    return (
      feedProviderGames
        // @ts-expect-error TODO: move useVirtualSportsStore to TypeScript
        ?.map((gameId: string) => games.data?.[gameId])
        .filter(Boolean) || []
    );
  }, [providers, games, provider]);

  const menuItemsDesktop: IVirtualSportsLobbyMenuItem[] = useMemo(() => {
    return sports.map(({ categoryId, categoryName }) => ({
      title: categoryName,
      icon: getSportIcon(categoryId as SportIconId),
      link: `${virtualSportsFrame}/${provider}/${categoryId}`,
      onClick: () => {
        logAction(LOG_ACTIONS.VSPORT_INTERNAL_NAV, {
          ProviderName: t(`virtualSports.${provider}`),
          GameName: categoryName
        });
      }
    }));
  }, [sports]);

  useEffect(() => {
    const parentElement = wrapperRef.current?.parentNode as HTMLElement | null;

    if (isFeedLoaded && !sports.length && parentElement) {
      parentElement.style.display = 'none';
    }
  }, [isFeedLoaded]);

  if (!isDesktop) {
    return (
      <Box
        data-testid="vs-lobby-menu-mobile"
        flex="1 0 auto"
        className={styles.slider}
      >
        {sports.map(({ categoryId, categoryName }) => (
          <GridItem
            key={categoryId}
            component={LocalizedNavLink}
            to={`${virtualSportsFrame}/${provider}/${categoryId}`}
            text={categoryName}
            iconName={getSportIcon(categoryId as SportIconId)}
            size={isMobile ? 'xs' : 'm'}
            className={styles.sliderItem}
            actionType="color2"
          />
        ))}
      </Box>
    );
  }

  return (
    <Box
      data-testid="vs-lobby-menu-desktop"
      ref={wrapperRef}
      direction="column"
      className={styles.lobbyWrapper}
    >
      <Box align="center" className={styles.title}>
        <AText breed="BU" size="xl" color="sm_txt_color_3">
          {t(`virtualSports.${provider}`)}
        </AText>
      </Box>
      <ul className={styles.menu}>
        {menuItemsDesktop.map(({ link, title, icon, onClick }) => (
          <li key={link}>
            <LocalizedNavLink
              className={styles.link}
              activeClassName={styles.active}
              onClick={onClick}
              to={link}
            >
              <Box align="center" flex="1">
                <Box className={styles.iconWrap}>
                  <Icon name={icon} size="s" className={styles.icon} />
                </Box>
                <AText breed="R" className={styles.itemTitle}>
                  {title}
                </AText>
              </Box>
            </LocalizedNavLink>
          </li>
        ))}
      </ul>
    </Box>
  );
};

export default LobbyMenu;
